import React, { useEffect } from "react";
import type { NextPage, NextPageContext } from "next";
import getConfig from "next/config";
import Head from "next/head";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import parse from "html-react-parser";
import ArticleContent from "@pageContent/ArticleContent";
import OopsPage from "@pageContent/OopsPage";
import { tableNames } from "@constants/consts";
import { useHostConfig } from "@utils/common/getHostConfig";
import { useNoticeError } from "@utils/common/newRelic";
import usePageView from "@utils/common/usePageView";
import { fetchDpgAds, injectSnippet } from "@utils/pageContent/displayAds";
import { fetchByEndpoint } from "@utils/pageContent/fetchData";

const {
  publicRuntimeConfig: { cloudinaryDomain },
} = getConfig();

const ArticleMetadata: React.FC<{ article?: Article }> = ({ article }) => {
  const { outletName } = useHostConfig();
  if (!article) return null;
  const { structuredData } = article;
  return (
    <Head>
      <title>{`${structuredData?.headline || article.title} | ${structuredData?.author.name || outletName}`}</title>
      <meta property="og:title" content={article.title} />
      <meta property="og:type" content="article" />
      {article.category && <meta property="article:section" content={article.category.title} />}
      {structuredData && (
        <>
          {structuredData.description && <meta name="description" content={structuredData.description} />}
          <meta property="og:url" content={structuredData.mainEntityOfPage.url} />
          <meta property="og:description" content={structuredData.description} />
          <meta
            property="og:image"
            content={`${cloudinaryDomain}/image/fetch/w_1200,h_627,c_fill,q_auto,f_auto/${structuredData.thumbnailUrl}`}
          />
          <meta property="article:published_time" content={structuredData.datePublished} />
          <meta property="article:modified_time" content={structuredData.dateModified} />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(article.structuredData),
            }}
          />
        </>
      )}
    </Head>
  );
};

const ArticlePage: NextPage<{
  initialArticle?: Article;
  initialAds?: DpgAdsResponse | null;
}> = ({ initialArticle, initialAds }) => {
  const { query, asPath } = useRouter();

  const slugs = Array.isArray(query.slugs) ? query.slugs : [];
  const articleSlug = slugs[slugs.length - 1];

  const articleQueryKey = ["article", articleSlug];

  const {
    isLoading,
    data: article,
    isError,
    error,
  } = useQuery<Article>({
    queryKey: articleQueryKey,
    queryFn: () => fetchByEndpoint<Article>(`/api/article/${articleSlug}`),
    initialData: initialArticle,
  });

  usePageView({
    isLoading,
    error,
    data: article,
  });

  useEffect(() => {
    if (asPath.includes("zoek=")) return;
    if (initialAds && !window.dpgPreviousRequest) {
      window.dpgPreviousRequest = initialAds.requestBody;
      window.eventBus.dispatch("triggerAd", { hasInitialAds: true });
      return;
    }

    fetchDpgAds(asPath).then((data) => {
      if (data) {
        injectSnippet(data.html);
        window.eventBus.dispatch("triggerAd", {});
      }
    });
  }, [initialAds, asPath]);

  useNoticeError(error, { queryKey: articleQueryKey.join(","), articleSlug });

  if (!article && !isLoading) return <OopsPage is404={!isError} />;
  const adHtml = parse(initialAds?.html || "");
  return (
    <>
      <Head>{adHtml}</Head>
      <>
        <ArticleMetadata article={article} />
        <ArticleContent article={article} isLoading={isLoading} />
      </>
    </>
  );
};

ArticlePage.getInitialProps = async ({
  req,
  res,
  query,
  dynamoDBUtils,
}: NextPageContext & { dynamoDBUtils: { getItemBySlug: <T>(tableName: string, slug: string) => T } }) => {
  if (!res || !req) return {};

  const slugs = Array.isArray(query.slugs) ? query.slugs : [];
  const articleSlug = slugs[slugs.length - 1];

  try {
    const initialArticle = await dynamoDBUtils.getItemBySlug<Article>(tableNames.article, `${articleSlug}`);

    if (initialArticle) {
      const [currentUrl] = req.url?.split("?") || [];
      const initialAds = await fetchDpgAds(currentUrl);

      if (initialArticle.redirectUrl || currentUrl !== initialArticle.url) {
        res.writeHead(301, { Location: initialArticle.redirectUrl || initialArticle.url });
        res.end();
        return {};
      }

      return { initialArticle, initialAds };
    }
    res.statusCode = 404;
  } catch (error) {
    res.statusCode = 500;
  }
  return {};
};

export default ArticlePage;
